// MF-554 — Pure helpers for the Board Builder grid (12 columns, fixed-height rows — D7).
export const COLS = 12

/** A new tile id: item code + a short time-based suffix (backend accepts [A-Za-z0-9_-]{1,40}). */
export const newTileId = (itemCode = 'tile') =>
  `${String(itemCode).slice(0, 30)}-${Date.now().toString(36).slice(-6)}`.replace(/[^A-Za-z0-9_-]/g, '-')

/** Minimum / default size of a Library item (falls back to a small tile). */
export const sizeOf = (item) => ({
  minW: item?.layout?.minW || 2,
  minH: item?.layout?.minH || 1,
  w: Math.min(item?.layout?.defaultW || 4, COLS),
  h: item?.layout?.defaultH || 2
})

/** First row below every tile — where a new tile goes. */
export const bottomRow = (tiles = []) => tiles.reduce((max, t) => Math.max(max, t.y + t.h), 0)

/** Tiles → react-grid-layout layout (with each item's minimum size). */
export const toGridLayout = (tiles = [], itemsByCode = new Map()) =>
  tiles.map((t) => {
    const { minW, minH } = sizeOf(itemsByCode.get(t.itemCode))
    return { i: t.id, x: t.x, y: t.y, w: t.w, h: t.h, minW: Math.min(minW, COLS), minH }
  })

/** react-grid-layout layout → tiles with the new positions/sizes (overrides untouched). */
export const applyGridLayout = (tiles = [], layout = []) => {
  const byId = new Map(layout.map((l) => [l.i, l]))
  return tiles.map((t) => {
    const l = byId.get(t.id)
    return l ? { ...t, x: l.x, y: l.y, w: l.w, h: l.h } : t
  })
}

/** Same tiles, same places? (avoid marking the Board dirty on react-grid-layout's initial echo) */
export const sameLayout = (a = [], b = []) =>
  a.length === b.length &&
  a.every((t, i) => {
    const o = b[i]
    return o && t.id === o.id && t.x === o.x && t.y === o.y && t.w === o.w && t.h === o.h
  })

const collide = (a, b) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h

/**
 * Vertical compaction with collision resolution: every tile keeps its column span and floats up to
 * the first row where it overlaps nothing placed before it (reading order). Guarantees tiles never
 * overlap — in the editor after add/drop/move, before saving, and defensively in the viewer.
 * @returns {Array} tiles in their original order, with fixed y (and x clamped to the grid)
 */
export const compactTiles = (tiles = []) => {
  const order = tiles.map((t, i) => ({ t, i })).sort((a, b) => a.t.y - b.t.y || a.t.x - b.t.x)
  const placed = []
  const out = new Array(tiles.length)
  for (const { t, i } of order) {
    const w = Math.min(Math.max(1, t.w), COLS)
    const cand = { ...t, w, x: Math.min(Math.max(0, t.x), COLS - w), y: 0 }
    while (placed.some((p) => collide(p, cand))) cand.y += 1
    placed.push(cand)
    out[i] = cand
  }
  return out
}

/**
 * Tiles saved before an item's minimum size grew (e.g. the KPI card now needs 2 rows) are enlarged to
 * that minimum, then compacted so the taller tile pushes the ones below it down.
 * @returns {Array|null} the fixed tiles, or null when every tile already fits
 */
export const fitMinimums = (tiles = [], itemsByCode = new Map()) => {
  let changed = false
  const grown = tiles.map((t) => {
    const item = itemsByCode.get(t.itemCode)
    if (!item) return t
    const { minW, minH } = sizeOf(item)
    const w = Math.max(t.w, Math.min(minW, COLS))
    const h = Math.max(t.h, minH)
    if (w === t.w && h === t.h) return t
    changed = true
    return { ...t, w, h }
  })
  return changed ? compactTiles(grown) : null
}
